import { Component, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SecurityService } from 'src/app/security/services/security.service';
import { NavigationButtonNode } from 'src/app/utils/model/navigation.model';
import { SidebarStateFunction } from '../sidebar-menu.function';
import { APP_ROUTES } from 'src/app/app-routing.const';
import { SecurityRole } from 'src/app/utils/enums/enums/general/role.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
  standalone: false
})
export class SidebarMenuComponent {

    readonly COLLAPSE_THRESHOLD = 1000;

  isBelowThreshold: boolean = window.innerWidth < this.COLLAPSE_THRESHOLD;

  menu: NavigationButtonNode[] = [];

  constructor(
    private router: Router,
    private securityService: SecurityService,
    protected sidebar: SidebarStateFunction
  )
    {
      this.initSubscription();
    }


  @HostListener('window:resize')
  onResize() {
    this.isBelowThreshold = window.innerWidth < this.COLLAPSE_THRESHOLD;
    if (this.isBelowThreshold) {
      this.sidebar.setCollapsed(false);   // force full text, never icon rail
    }
  }

  @HostListener('window:keydown.escape')
  onEscape() {
    this.sidebar.closeMobile();
  }

  initSubscription(){
    this.securityService.authState$
    .pipe(takeUntilDestroyed())
    .subscribe(isLoggedIn => {
      if (isLoggedIn) this.refreshMenu();
    });

    // On navigation: close mobile drawer + switch menu when entering/leaving settings
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.sidebar.closeMobile();
        if (this.isSettingsUrl()) this.sidebar.setCollapsed(false);
        this.refreshMenu();
    });
  }

  // =============================================
  // Menu selection
  // =============================================
  private currentContext: 'base' | 'settings' | 'organisation' | null = null;

  private refreshMenu() {
    const context = this.isSettingsUrl() ? 'settings'
      : this.isOrganisationUrl() ? 'organisation'
      : 'base';

    if (context === this.currentContext && this.menu.length){
      return;
    }
    this.currentContext = context;

    this.menu = context === 'settings'     ? this.SETTINGS_MENU
              : context === 'organisation' ? this.ORGANISATION_MENU
              : this.buildMenu();
  }

  protected isSettingsUrl(): boolean {
    return this.router.url.startsWith(APP_ROUTES.SETTINGS.ROOT);
  }

  protected isCorporateUrl(): boolean {
    return this.router.url.startsWith(APP_ROUTES.CORPORATE);
  }

  protected isOrganisationUrl(): boolean {
    return this.router.url.startsWith(APP_ROUTES.ORGANISATION.ROOT)
        && !this.router.url.includes(APP_ROUTES.ORGANISATION.LIST);
  }

  protected isContextMenu(): boolean {
    return this.isSettingsUrl() || this.isOrganisationUrl();
  }

  // =============================================
  // Contextual menus
  // =============================================

  private readonly ORGANISATION_MENU: NavigationButtonNode[] = [
    { name: 'go_back', image: 'assets/logos/settings/arrow-left.svg',
      action: () => this.router.navigate([APP_ROUTES.DASHBOARD]) },

    { isDivider: true, name: 'div_org' },

    { name: 'org_identification', image: 'assets/logos/organisation/identification-icon.svg', route: APP_ROUTES.ORGANISATION.IDENTIFICATION },
    { name: 'org_members',        image: 'assets/logos/organisation/user-icon.svg',           route: APP_ROUTES.ORGANISATION.USERS },
    { name: 'org_teams',          image: 'assets/logos/organisation/team-icon.svg',           route: APP_ROUTES.ORGANISATION.TEAMS },
    { name: 'org_rights',         image: 'assets/logos/organisation/right-icon.svg',          route: APP_ROUTES.ORGANISATION.RIGHTS },
    { name: 'org_api',            image: 'assets/logos/organisation/api-icon.svg',            route: APP_ROUTES.ORGANISATION.API },
    { name: 'org_logs',           image: 'assets/logos/organisation/form-icon.svg',           route: APP_ROUTES.ORGANISATION.LOGS }
  ];

  private readonly CORPORATE_MENU: NavigationButtonNode[] = [
    { name: 'go_back', image: 'assets/logos/settings/arrow-left.svg',
      action: () => this.router.navigate([APP_ROUTES.DASHBOARD]) },

    { isDivider: true, name: 'div_org' }
  ];


  // =============================================
  // Settings menu (/settings/**)
  // =============================================

  private readonly SETTINGS_MENU: NavigationButtonNode[] = [
    { name: 'go_back', image: 'assets/logos/settings/arrow-left.svg',
      action: () => this.router.navigate([APP_ROUTES.DASHBOARD]) },

    { isDivider: true, name: 'div_settings' },

    { name: 'identity',     image: 'assets/logos/settings/user-icon.svg',         route: APP_ROUTES.SETTINGS.PROFILE },
    { name: 'notification', image: 'assets/logos/settings/notification-icon.svg', route: APP_ROUTES.SETTINGS.NOTIFICATION },
    { name: 'security',     image: 'assets/logos/settings/security-icon.svg',     route: APP_ROUTES.SETTINGS.SECURITY },
    { name: 'language',     image: 'assets/logos/settings/language-icon.svg',     route: APP_ROUTES.SETTINGS.LANGUAGE }
  ];


  // =============================================
  // Menu definition
  // =============================================

  private readonly BASE_MENU: NavigationButtonNode[] = [
    // --- Contract Workspace ---
    { isLabel: true, labelKey: 'navigation.subtitle.contract_workspace', name: 'label_cw' },
    { name: 'dashboard',           image: 'assets/logos/main-sidebar/dashboard.svg',            route: APP_ROUTES.DASHBOARD },
    { name: 'contract_repository', image: 'assets/logos/main-sidebar/contracts-repository.svg', route: APP_ROUTES.REPOSITORY,
      action: () => this.router.navigate([this.hasRole(SecurityRole.EXTERNAL) ? '/external/repository' : APP_ROUTES.REPOSITORY]) },
    { name: 'pending_contract',    image: 'assets/logos/main-sidebar/pending-contracts.svg',    route: APP_ROUTES.PENDING },

    { isDivider: true, name: 'div1' },

    // --- Entities ---
    { isLabel: true, labelKey: 'navigation.subtitle.entities', name: 'label_entities' },
    { name: 'entities',  image: 'assets/logos/main-sidebar/entities.svg', route: APP_ROUTES.ENTITIES },
    { name: 'key_data',  image: 'assets/logos/main-sidebar/key-data.svg',
      children: [
        { name: 'key_data_list',     image: 'assets/logos/main-sidebar/key-data-list.svg',          route: APP_ROUTES.KEYDATA },
        { name: 'key_data_set', image: 'assets/logos/main-sidebar/key_data_set_icon.svg', route: APP_ROUTES.KEYDATASET }
      ]
    },
    { name: 'templates', image: 'assets/logos/main-sidebar/templates.svg', route: APP_ROUTES.TEMPLATE.ROOT_PAGE },

    { isDivider: true, name: 'div2' },

    // --- Supervision ---
    { isLabel: true, labelKey: 'navigation.subtitle.supervision', name: 'label_supervision' },
    { name: 'regulatory_reports',      image: 'assets/logos/main-sidebar/regulatory-registers.svg',         route: APP_ROUTES.REGISTER.ROOT },
    { name: 'regulatory_notification', image: 'assets/logos/main-sidebar/regulatory-notification-icon.svg', route: APP_ROUTES.REGULATORY.NOTIFICATION },

    { isDivider: true, name: 'div3' },

    // --- Administration ---
    { isLabel: true, labelKey: 'navigation.subtitle.administration', name: 'label_admin' },
    { name: 'settings',           image: 'assets/logos/main-sidebar/settings.svg',                 route: APP_ROUTES.SETTINGS.PROFILE },
    { name: 'organisation',       image: 'assets/logos/main-sidebar/organisation.svg',             route: APP_ROUTES.ORGANISATION.IDENTIFICATION },
    { name: 'organisation_admin', image: 'assets/logos/main-sidebar/organisation-admin.svg',             route: APP_ROUTES.ORGANISATION.LIST },
    { name: 'corporate',          image: 'assets/logos/main-sidebar/corporate-structure-icon.svg', route: APP_ROUTES.CORPORATE },
    { name: 'matrix',             image: 'assets/logos/main-sidebar/matrix-icon.svg',              route: APP_ROUTES.MATRIX },

    { isDivider: true, name: 'div4' },

    // --- Logout (behavior, not navigation) ---
    { name: 'logout', image: 'assets/logos/main-sidebar/logout.svg', isDanger: true,
      action: () => this.securityService.logout() }
  ];

  private hasRole(role: SecurityRole): boolean {
    return this.securityService.isSecurityRoleUser(role);
  }

  private buildMenu(): NavigationButtonNode[] {
    const isExternal = this.hasRole(SecurityRole.EXTERNAL);
    const isAdmin    = this.hasRole(SecurityRole.ADMINISTRATOR);
    const isGovernys = this.hasRole(SecurityRole.GOVERNYS_ADMINISTRATOR);

    const hide = new Set<string>([
      ...((isExternal) ? ['dashboard', 'pending_contract', 'entities', 'key_data', 'templates',
                        'organisation', 'corporate', 'matrix', 'label_entities', 'div2', 'div3',
                        'label_supervision', 'regulatory_reports','regulatory_notification'] : []),
      ...((isAdmin) ? ['contract_repository', 'pending_contract', 'entities', 'key_data', 'templates',
                        'corporate', 'matrix', 'label_entities', 'div2', 'div3',
                        'label_supervision', 'regulatory_reports', 'regulatory_notification'] : []),
      ...(!(isAdmin || isGovernys)  ? ['organisation', 'corporate'] : []),
      ...(!isGovernys ? ['organisation_admin'] : [])
    ]);

    return this.BASE_MENU
      .filter(node => !hide.has(node.name || ''))
      .map(node => node.children
        ? { ...node, children: node.children.filter(c => !hide.has(c.name || '')) }
        : node);
  }

  // =============================================
  // mat-tree accessors
  // =============================================

  childrenAccessor = (node: NavigationButtonNode) => node.children ?? [];
  hasChild  = (_: number, node: NavigationButtonNode) => !!node.children?.length;
  isDivider = (_: number, node: NavigationButtonNode) => !!node.isDivider;
  isLabel   = (_: number, node: NavigationButtonNode) => !!node.isLabel;

  // =============================================
  // Active-route tracker
  // =============================================

  private currentPath(): string {
    return this.router.url.split('?')[0];
  }

  isActive(node: NavigationButtonNode): boolean {
    if (!node.route)
      return false;
    const path = this.currentPath();
    return path === node.route || path.startsWith(node.route + '/');
  }

  isChildActive(node: NavigationButtonNode): boolean {
    return !!node.children?.some(c => this.isActive(c) || this.isChildActive(c));
  }

  // =============================================
  // Interactions
  // =============================================

  onNodeClick(node: NavigationButtonNode) {
    if (node.action) {
      node.action();
    } else if (node.route) {
      this.router.navigate([node.route]);
    }
    this.sidebar.closeMobile();
  }
}
